// WheelBuilderCoursera.js — Coursera-branded Conduit wheel (stacked layout)
// - Wheel sits UNDER controls (single-column flow)
// - Conduit preset dropdown (5–100%), 100% closes ring
// - KPI-safe defaults, smooth sliders, no clipping, SVG/PNG export
// No external deps.

import React, { useMemo, useRef, useState, useEffect } from "react";

/* ============ 1) Tokens & helpers ============ */
const TOKENS = {
  blue500: "#2E89FF",
  blue700: "#2A73CC",
  blue900: "#1C3F7A",
  blue975: "#0E2B57",
  ink: "#0B1A3C",
  line: "#E6E7EB",
  bg: "#F5F7FA",
  card: "#FFFFFF",
};

const KPI_SLICES = [
  { label: "Opportunities Influenced", value: 20, color: "#2A73CC" },
  { label: "Pipeline Value Influenced", value: 25, color: "#225FB0" },
  { label: "Closed-Won Revenue", value: 20, color: "#184C94" },
  { label: "Faster Sales Cycle", value: 10, color: "#2F7DD9" },
  { label: "Avg. Follow-Up Rate", value: 20, color: "#3962A9" },
  { label: "Hours to First Touch", value: 5, color: "#5C8EE1" },
];

const CONDUIT_PRESETS = [
  5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95,
  100,
].map((pct) => ({
  pct,
  deg: Math.max(0, (100 - pct) * 3.6),
  label: pct === 100 ? "100% (closed ring)" : `${pct}% Coursera “C”`,
}));

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const polarToXY = (cx, cy, r, deg) => {
  const a = (deg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};
const arcPath = (cx, cy, rO, rI, start, end) => {
  const sweep = end - start,
    large = sweep > 180 ? 1 : 0;
  const p1 = polarToXY(cx, cy, rO, start),
    p2 = polarToXY(cx, cy, rO, end);
  const p3 = polarToXY(cx, cy, rI, end),
    p4 = polarToXY(cx, cy, rI, start);
  return [
    `M ${p1.x} ${p1.y}`,
    `A ${rO} ${rO} 0 ${large} 1 ${p2.x} ${p2.y}`,
    `L ${p3.x} ${p3.y}`,
    `A ${rI} ${rI} 0 ${large} 0 ${p4.x} ${p4.y}`,
    "Z",
  ].join(" ");
};

// exports
function downloadSVG(node, name = "wheel.svg") {
  if (!node) return;
  const blob = new Blob([node.outerHTML], {
    type: "image/svg+xml;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
function downloadPNG(node, size, scale = 2, name = "wheel.png") {
  if (!node) return;
  const svg = new Blob([node.outerHTML], {
    type: "image/svg+xml;charset=utf-8",
  });
  const url = URL.createObjectURL(svg);
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = size * scale;
    c.height = size * scale;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => {
      const out = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = out;
      a.download = name;
      a.click();
      URL.revokeObjectURL(out);
      URL.revokeObjectURL(url);
    }, "image/png");
  };
  img.src = url;
}

/* ============ 2) Component ============ */
export default function WheelBuilderCoursera({
  titleDefault = "QUALIFIED PIPELINE — KPI VIEW",
}) {
  const [title, setTitle] = useState(titleDefault);
  const [subtitle, setSubtitle] = useState("");
  const [centerText, setCenterText] = useState("QUALIFIED\nPIPELINE");

  const [innerHolePct, setInnerHolePct] = useState(58);
  const [labelRadiusPct, setLabelRadiusPct] = useState(78);
  const [openingDeg, setOpeningDeg] = useState(CONDUIT_PRESETS[18].deg); // 95%
  const [preset, setPreset] = useState(CONDUIT_PRESETS[18].pct);

  const [fontFamily, setFontFamily] = useState(
    `"Source Sans Pro","Source Sans 3",system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif`
  );
  const [titleSize, setTitleSize] = useState(22);
  const [centerSize, setCenterSize] = useState(20);
  const [labelSize, setLabelSize] = useState(12);

  const [slices, setSlices] = useState(KPI_SLICES);

  // canvas (kept generous and centered)
  const size = 760,
    cx = size / 2,
    cy = size / 2;
  const outerR = size * 0.4,
    innerR = outerR * (innerHolePct / 100),
    labelR = outerR * (labelRadiusPct / 100);

  useEffect(() => {
    const m = CONDUIT_PRESETS.find(
      (p) => Math.round(p.deg) === Math.round(openingDeg)
    );
    if (m && m.pct !== preset) setPreset(m.pct);
  }, [openingDeg]); // sync preset when dragging
  const onPreset = (pct) => {
    const p = CONDUIT_PRESETS.find((x) => x.pct === Number(pct));
    if (p) {
      setPreset(p.pct);
      setOpeningDeg(p.deg);
    }
  };

  const total = useMemo(
    () => slices.reduce((s, x) => s + (Number(x.value) || 0), 0),
    [slices]
  );

  const segments = useMemo(() => {
    const budget = 360 - clamp(openingDeg, 0, 360);
    let acc = 0;
    return slices.map((s) => {
      const portion = total > 0 ? (Number(s.value) || 0) / total : 0;
      const start = acc * budget,
        end = (acc + portion) * budget;
      acc += portion;
      const rot = clamp(openingDeg, 0, 360) + 30;
      const startA = start + rot,
        endA = end + rot;
      const path = arcPath(cx, cy, outerR, innerR, startA, endA);
      const mid = (startA + endA) / 2,
        lp = polarToXY(cx, cy, labelR, mid);
      return { ...s, path, labelX: lp.x, labelY: lp.y };
    });
  }, [slices, total, openingDeg, outerR, innerR, labelR]);

  const setSlice = (i, k, v) =>
    setSlices((p) => p.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const addSlice = () =>
    setSlices((p) => [
      ...p,
      { label: "New KPI", value: 10, color: TOKENS.blue700 },
    ]);
  const removeSlice = (i) => setSlices((p) => p.filter((_, idx) => idx !== i));

  const svgRef = useRef(null);
  const lines = (centerText || "").split("\n").filter(Boolean);

  return (
    <div style={{ background: TOKENS.bg, padding: 16 }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Card: controls */}
        <section style={card()}>
          <h2 style={{ margin: "0 0 8px", fontSize: 18, color: TOKENS.ink }}>
            For Business
          </h2>
          <p style={{ margin: "0 0 12px", color: "#5C6570", fontSize: 13 }}>
            Use the Conduit-shaped wheel to summarize this week’s hot-list by
            stage. Keep 6–8 slices for readability; export as SVG/PNG for decks
            or the dashboard.
          </p>

          {/* Controls grid */}
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <label style={lbl()}>Top Title</label>
              <input
                style={inp()}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label style={lbl()}>Subtitle</label>
              <input
                style={inp()}
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
              />
            </div>
            <div>
              <label style={lbl()}>Center text (\n new line)</label>
              <textarea
                rows={3}
                style={inp()}
                value={centerText}
                onChange={(e) => setCenterText(e.target.value)}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 8,
              }}
            >
              <Num label="Title size" val={titleSize} set={setTitleSize} />
              <Num label="Center size" val={centerSize} set={setCenterSize} />
              <Num label="Label size" val={labelSize} set={setLabelSize} />
            </div>

            <div>
              <label style={lbl()}>Font family</label>
              <input
                style={inp()}
                value={fontFamily}
                onChange={(e) => setFontFamily(e.target.value)}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <Slider
                label="Inner hole (% radius)"
                min={40}
                max={80}
                val={innerHolePct}
                set={setInnerHolePct}
              />
              <Slider
                label="Label radius (% outer)"
                min={65}
                max={92}
                val={labelRadiusPct}
                set={setLabelRadiusPct}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <div>
                <label style={lbl()}>Conduit preset</label>
                <select
                  style={inp()}
                  value={preset}
                  onChange={(e) => onPreset(Number(e.target.value))}
                  aria-label="Conduit preset percent"
                >
                  {CONDUIT_PRESETS.map((p) => (
                    <option key={p.pct} value={p.pct}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lbl()}>
                  Conduit opening (deg){preset === 100 ? " — 0" : ""}
                </label>
                <input
                  type="range"
                  min={0}
                  max={180}
                  step={1}
                  value={openingDeg}
                  onChange={(e) => setOpeningDeg(Number(e.target.value))}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Export row */}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ color: "#6B7280", fontSize: 12 }}>
                Total = <strong aria-live="polite">{total}</strong>
              </div>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button
                  onClick={() => downloadSVG(svgRef.current, "wheel.svg")}
                  style={btn("primary")}
                >
                  Download SVG
                </button>
                <button
                  onClick={() =>
                    downloadPNG(svgRef.current, size, 2, "wheel@2x.png")
                  }
                  style={btn()}
                >
                  Download PNG (2×)
                </button>
                <button
                  onClick={() =>
                    downloadPNG(svgRef.current, size, 3, "wheel@3x.png")
                  }
                  style={btn()}
                >
                  Download PNG (3×)
                </button>
              </div>
            </div>

            <hr style={{ border: 0, borderTop: `1px solid ${TOKENS.line}` }} />

            {/* Slices editor */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontSize: 16, color: TOKENS.ink }}>
                Slices
              </h3>
              <button onClick={addSlice} style={btn()}>
                + Add Slice
              </button>
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {slices.map((s, i) => (
                <div
                  key={i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.4fr 0.6fr 0.4fr auto",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <input
                    style={inp()}
                    value={s.label}
                    onChange={(e) => setSlice(i, "label", e.target.value)}
                    placeholder="Label"
                  />
                  <input
                    type="number"
                    style={inp()}
                    value={s.value}
                    onChange={(e) =>
                      setSlice(i, "value", Number(e.target.value))
                    }
                    placeholder="Value"
                  />
                  <input
                    type="color"
                    value={s.color}
                    onChange={(e) => setSlice(i, "color", e.target.value)}
                    title="Color"
                    style={{
                      height: 38,
                      border: `1px solid ${TOKENS.line}`,
                      borderRadius: 8,
                    }}
                  />
                  <button onClick={() => removeSlice(i)} style={btn("danger")}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Card: wheel/canvas (stacked UNDER controls) */}
        <section style={card()}>
          <div style={{ marginBottom: 8, color: "#6B7280", fontSize: 12 }}>
            Tailored visual — “Hot list” and journey by stage
          </div>
          <div
            style={{
              border: `1px solid ${TOKENS.line}`,
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <svg
              ref={svgRef}
              width={size}
              height={size}
              viewBox={`0 0 ${size} ${size}`}
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="0" y="0" width={size} height={size} fill="#ffffff" />
              <text
                x={cx}
                y={36}
                fontFamily={fontFamily}
                fontSize={titleSize}
                fontWeight="800"
                textAnchor="middle"
                fill={TOKENS.blue975}
              >
                {title}
              </text>
              {subtitle?.trim() ? (
                <text
                  x={cx}
                  y={36 + titleSize + 6}
                  fontFamily={fontFamily}
                  fontSize={Math.max(12, titleSize * 0.6)}
                  fontWeight="400"
                  textAnchor="middle"
                  fill="#5C6570"
                >
                  {subtitle}
                </text>
              ) : null}
              {segments.map((seg, idx) => (
                <path
                  key={idx}
                  d={seg.path}
                  fill={seg.color || TOKENS.blue700}
                  stroke="#fff"
                  strokeWidth="2"
                />
              ))}
              {segments.map((seg, idx) => {
                const label = seg.label || "";
                const max = 28;
                const show =
                  label.length > max ? label.slice(0, max) + "…" : label;
                return (
                  <text
                    key={`lbl-${idx}`}
                    x={seg.labelX}
                    y={seg.labelY}
                    fontFamily={fontFamily}
                    fontSize={labelSize}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="#fff"
                    fontWeight="700"
                    style={{ pointerEvents: "none" }}
                  >
                    {show}
                  </text>
                );
              })}
              {lines.map((ln, i) => (
                <text
                  key={i}
                  x={cx}
                  y={
                    cy -
                    ((lines.length - 1) * (centerSize * 1.2)) / 2 +
                    i * (centerSize * 1.2)
                  }
                  fontFamily={fontFamily}
                  fontSize={centerSize}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={TOKENS.blue975}
                  fontWeight="900"
                >
                  {ln}
                </text>
              ))}
            </svg>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ============ 3) Small UI helpers ============ */
function card() {
  return {
    background: TOKENS.card,
    border: `1px solid ${TOKENS.line}`,
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  };
}
function inp() {
  return {
    width: "100%",
    padding: "10px 12px",
    border: `1px solid ${TOKENS.line}`,
    borderRadius: 8,
    background: "#fff",
    fontSize: 14,
  };
}
function lbl() {
  return { display: "block", fontSize: 12, color: "#5C6570", marginBottom: 4 };
}
function btn(kind) {
  const base = {
    padding: "10px 14px",
    borderRadius: 12,
    border: `1px solid ${TOKENS.line}`,
    background: "#fff",
    cursor: "pointer",
    fontWeight: 600,
  };
  if (kind === "primary")
    return {
      ...base,
      background: TOKENS.blue700,
      borderColor: TOKENS.blue700,
      color: "#fff",
    };
  if (kind === "danger") return { ...base, borderColor: "#D33", color: "#D33" };
  return base;
}
function Num({ label, val, set }) {
  return (
    <div>
      <label style={lbl()}>{label}</label>
      <input
        type="number"
        style={inp()}
        value={val}
        onChange={(e) => set(Number(e.target.value) || 0)}
      />
    </div>
  );
}
function Slider({ label, min, max, val, set }) {
  return (
    <div>
      <label style={lbl()}>{label}</label>
      <input
        type="range"
        min={min}
        max={max}
        value={val}
        onChange={(e) => set(Number(e.target.value))}
        style={{ width: "100%" }}
      />
    </div>
  );
}
